import React, { FC, JSXElementConstructor } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import GroupedByBundleItem from '../bundles/GroupedByItem';
import { changeGroupBy } from '../../store/grouping/actions';
import * as types from '../../store/constants';
import { ListContentAction } from '../../store/constants';
import { GroupingState } from '../../store/grouping/state';
import { AppDispatch } from '../../store';
import { getGroupItemId, GroupItem } from '../../../models/GroupItem';
import { Orderlist } from '../../../models/OrderList';
import { ProductListItemProps } from './ProductList';
import classNames from 'classnames';
import { DisplayTypeState } from '../../store/displayType/state';
import { useDispatch } from 'react-redux';
import { ListProduct, ProductMetaData, UsageCondition } from '../../../models/Product';
import { UpdateProductOptions } from '../../store/products/actionsCart';
import { Article, SubscriptionPeriodType } from '../../../models/Article';
import { ProductSelectionState } from '../../store/productSelection/state';
import { deleteURLGroupByEntry, replaceURLGroupByEntry } from './group-by-utils';
import { BulkAddProductsToSelectionListResponse, BulkAddProductsToShoppingCartResponse } from '../../store/api';
import { SingleCopyOrMoveOptions } from '../../../models/ListContentCheckOptions';

export type GroupedByItemProps = {
    readonly item: GroupItem;
    readonly grouping: GroupingState;
    readonly isGroupAddAction?: boolean;
    readonly showSelectedPriceOnly?: boolean;
    readonly allowSelection?: boolean;
    readonly totalProducts?: number;
    readonly currentPage?: number;
    readonly pageSize?: number;
    readonly displayType?: DisplayTypeState;
    readonly onPageChange: (page: number) => void;
    readonly onSelectProduct: (productId: string) => void;
    readonly onAddGroupedItemToCart?: (
        bundleId: string,
        performChecks: boolean
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onRemoveGroupedItemFromList?: (item: GroupItem) => void;
    readonly onGroupAmountChange?: (item: GroupItem, amount: number, command: ListContentAction) => void;
    readonly onCopyGroupToList: (
        item: GroupItem,
        list: Orderlist,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onMoveGroupToList: (
        item: GroupItem,
        list: Orderlist,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onGroupBy?: (groupBy: { readonly key: string; readonly label?: string }) => void;
    readonly onCreateNewListClicked?: (name: string) => void;
    readonly onToggleList?: (itemId: string) => void;
    readonly tileItemComponent?: JSXElementConstructor<ProductListItemProps>;
    readonly listItemComponent?: JSXElementConstructor<ProductListItemProps>;
    readonly onCopyAmountChange?: (options: UpdateProductOptions, amount: number, command: string) => void;
    readonly onAddPrice: (options: SingleCopyOrMoveOptions) => Promise<void>;
    readonly onRemoveProductFromCart?: (product: ProductMetaData & Article) => void;
    readonly onUpdateUsageCondition?: (
        product: ProductMetaData & Article,
        newUsageCondition: UsageCondition,
        newSubscriptionPeriodType?: SubscriptionPeriodType
    ) => void;
    readonly productSelection?: ProductSelectionState;
    readonly currentListId?: string;
};

type Props = RouteComponentProps & {
    readonly groups: ReadonlyArray<GroupItem>;
    readonly grouping: GroupingState;
    readonly isGroupAddAction?: boolean;
    readonly deselectAll: () => void;
    readonly dispatch: AppDispatch;
    readonly products?: ListProduct[];
    readonly onPageChange: (page: number) => void;
    readonly onSelectProduct: (productId: string) => void;
    readonly onAddGroupedItemToCart?: (
        bundleId: string,
        performChecks: boolean
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onRemoveGroupedItemFromList?: (item: GroupItem) => void;
    readonly onGroupAmountChange?: (item: GroupItem, amount: number, command: ListContentAction) => void;
    readonly onCopyGroupToList: (
        item: GroupItem,
        list: Orderlist,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onMoveGroupToList: (
        item: GroupItem,
        list: Orderlist,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onGroupBy?: (groupBy: { readonly key: string; readonly label?: string }) => void;
    readonly onCreateNewListClicked?: (newSelectionListName: string) => void;
    readonly invalidateAggregations: () => Promise<void>;
    readonly itemComponent: JSXElementConstructor<GroupedByItemProps>;
    readonly tileItemComponent?: JSXElementConstructor<ProductListItemProps>;
    readonly listItemComponent?: JSXElementConstructor<ProductListItemProps>;
    readonly totalProducts?: number;
    readonly currentPage?: number;
    readonly pageSize?: number;
    readonly onCopyAmountChange?: (options: UpdateProductOptions, amount: number, command: string) => void;
    readonly onAddPrice: (options: SingleCopyOrMoveOptions) => Promise<void>;
    readonly displayType?: DisplayTypeState;
    readonly onRemoveProductFromCart?: (product: ProductMetaData & Article) => void;
    readonly onRemoveProductFromList?: (product: ProductMetaData & Article) => void;
    readonly onUpdateUsageCondition?: (
        product: ProductMetaData & Article,
        newUsageCondition: UsageCondition,
        newSubscriptionPeriodType?: SubscriptionPeriodType
    ) => Promise<void>;
    readonly productSelection?: ProductSelectionState;
    readonly currentListId?: string;
};

const GroupedByList: FC<Props> = props => {
    const dispatch = useDispatch();

    function toggleList(groupItemId: string) {
        const id = groupItemId === props.grouping.id ? null : groupItemId;

        const newGrouping = { ...props.grouping, ...{ id } };
        dispatch(changeGroupBy({ ...props.grouping, ...{ id } }));

        if (id || (!id && newGrouping.key !== types.GROUPBY_PRODUCTS)) {
            replaceURLGroupByEntry(newGrouping);
            props.deselectAll();
            props.invalidateAggregations().then();
            props.onPageChange(0);
        } else {
            deleteURLGroupByEntry();
        }
    }

    const ItemComponent = props.itemComponent;
    return (
        <div className={classNames('groupby', props.grouping.key)}>
            {props.groups.map(item => {
                return <ItemComponent {...props} key={getGroupItemId(item)} item={item} onToggleList={toggleList} />;
            })}
        </div>
    );
};

(GroupedByList as any).defaultProps = {
    groups: [],
    grouping: {},
    itemComponent: GroupedByBundleItem
};

export default withRouter(GroupedByList);
